import React, { createContext, useEffect, useReducer, useState } from "react";
import uniqueId from "lodash/uniqueId";
import get from "lodash/get";
import axios from "axios";

import { getSorName } from "../../common/util";

/** time in seconds */
const waitTime = 18;
/** number of characters */
const maxMessageLength = 1000;

/**
 * @typedef {{
 *  id: string;
 *  type: "user" | "assistant";
 *  timestamp: Date;
 *  text: string;
 *  status: "pending" | "partial" | "error" | "complete";
 *  reaction?: "up" | "down";
 *  isGreeting?: boolean;
 *  eventId?: string;
 *  invocationId?: string;
 * }} AssistMessage
 * 
 * @typedef {{
 *  id: string;
 *  invocationId: string;
 *  partial: boolean;
 *  content: {
 *    parts: Array<{
 *      text?: string;
 *      functionResponse?: {
 *        name: string;
 *        response: {
 *          result: string;
 *        }
 *      }
 *    }>
 *  }
 * }} AssistAgentResponse
 */

/**
 * @type {React.Context<{
 *  sessionId?: string,
 *  messages: Array<AssistMessage>,
 *  addUserMessage: (text: string, product?: string) => Promise<void>,
 *  updateFeedback: (id: string, reaction?: "up" | "down") => Promise<void>,
 *  modalOpen: boolean,
 *  setModalOpen: (open: boolean) => void,
 *  boxOpen: boolean,
 *  setBoxOpen: (open: boolean) => void,
 *  wait: number,
 *  isAdmin?: boolean,
 *  initials?: string,
 *  selectedProduct?: { label: string, value: string },
 *  setSelectedProduct: (product: { label: string, value: string }) => void,
 *  products?: Array<{ label: string, value: string }>,
 *  productsError?: { message: string, status: string },
 *  }>}
 */
const AssistContext = createContext(null);

/**
 * @param {Array<AssistMessage>} messages
 * @param {{ type: string, payload: unknown }} action
 */
const assistReducer = (messages, action) => {
  /** @type {Array<AssistMessage>} */
  let newMessages;

  switch (action.type) {
    case "upsertMessages":
      newMessages = [...messages];
      action.payload.messages.forEach(({ id, ...rest }) => {
        if (id) {
          const index = newMessages.findIndex((value) => value.id === id);

          if (index >= 0) {
            const oldMessage = newMessages[index];
            newMessages.splice(index, 1, { ...oldMessage, ...rest });
          } else {
            newMessages.push({ id, ...rest });
          }
        }
      });

      return newMessages;
    case "removeMessages":
      newMessages = messages.filter(
        ({ id }) => action.payload.ids.findIndex((value) => value === id) < 0
      );

      return newMessages;
    default:
      return messages;
  }
};

/**
 * @param {AssistMessage | Array<AssistMessage>} messages
 */
const upsertMessages = (messages) => {
  if (!Array.isArray(messages)) messages = [messages];
  return { type: "upsertMessages", payload: { messages } };
};

const getAssistSessionStorageKey = (sessionEndpoint) => `apic-assist-session:${sessionEndpoint || 'default'}`;

const readStoredSessionId = (storageKey) => {
  try {
    const value = window.localStorage.getItem(storageKey);
    if (typeof value !== "string") {
      return null;
    }

    const normalizedValue = value.trim();
    return normalizedValue || null;
  } catch {
    return null;
  }
};

const normalizeSessionId = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  return normalizedValue || null;
};

const writeStoredSessionId = (storageKey, nextSessionId) => {
  try {
    if (nextSessionId) {
      window.localStorage.setItem(storageKey, nextSessionId);
      return;
    }

    window.localStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures and continue without session persistence.
  }
};

/**
 * @param {{
 *  sessionEndpoint: string,
 *  sseAskEndpoint: string,
 *  feedbackEndpoint: string,
 *  isAdmin: boolean,
 *  initials: string,
 *  region: string,
 *  children: React.ReactNode
 * }} props
 */
const AssistProvider = ({ sessionEndpoint, sseAskEndpoint, feedbackEndpoint, isAdmin, initials, region, children }) => {
  const sessionStorageKey = getAssistSessionStorageKey(sessionEndpoint);
  const [messages, dispatch] = useReducer(assistReducer, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [boxOpen, setBoxOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => readStoredSessionId(sessionStorageKey));
  const [wait, setWait] = useState(0);
  const [products, setProducts] = useState([]);
  const [productsError, setProductsError] = useState(null);
  /** @type {ReturnType<typeof useState<{ label: string, value: string }>>} */
  const [selectedProduct, setSelectedProduct] = useState(null);

  const updateSessionId = (nextSessionId) => {
    const validSessionId = normalizeSessionId(nextSessionId);
    setSessionId(validSessionId);
    writeStoredSessionId(sessionStorageKey, validSessionId);
  };

  const startSession = async ({ force = false } = {}) => {
    if (!sessionEndpoint || (!force && sessionId)) {
      return sessionId;
    }

    if (!force) {
      const storedSessionId = readStoredSessionId(sessionStorageKey);
      if (storedSessionId) {
        setSessionId(storedSessionId);
        return storedSessionId;
      }
    }

    try {
      const response = await axios.post(sessionEndpoint, {}, {
        headers: {
          "Content-Type": "application/json",
        },
        validateStatus: status => (status >= 200 && status < 300) || (status >= 400 && status < 500),
      });

      if (response.status === 409) {
        // session already exists - parse id from detail message
        /** @type {{ detail: string }} */
        const { detail } = response.data;
        const match = detail.match(/session already exists:\s*([a-f0-9\-]+)/i);

        if (match && match[1]) {
          const existingSessionId = normalizeSessionId(match[1]);
          updateSessionId(existingSessionId);
          return existingSessionId;
        } else {
          throw new Error("Failed to parse existing session ID.");
        }
      } else if (response.status >= 400 && response.status < 500) {
        throw new Error(`Failed to create session: ${response.status}`);
      } else {
        /** @type {{ id: string }} */
        const { id } = response.data;
        const newSessionId = normalizeSessionId(id);
        updateSessionId(newSessionId);
        return newSessionId;
      }
    } catch (error) {
      updateSessionId(null);
      return null;
    }
  };

  const askAssistant = async (text, activeSessionId) => {
    return axios.post(sseAskEndpoint, {
      text,
      session_id: activeSessionId,
      sessionId: activeSessionId,
    }, {
      headers: {
        "Content-Type": "application/json",
      },
      responseType: 'arraybuffer',
    });
  };

  /** @type { (text: string) => Promise<void> } */
  const addUserMessage = async (text) => {
    if (!text) return;

    const agentId = uniqueId("assist-agent-");
    /** @type {{ [key: string]: AssistMessage }} */
    const agentMessages = {};
    const userMessage = {
      id: uniqueId("assist-user-"),
      type: "user",
      timestamp: new Date(),
      text,
      status: "complete",
    };
    const agentMessage = {
      id: `${agentId}-0`,
      type: "assistant",
      text: "",
      status: "pending",
    };

    dispatch(upsertMessages([userMessage, agentMessage]));
    setModalOpen(true);
    setWait(waitTime);

    try {
      const messageText = userMessage.text + (isAdmin && selectedProduct?.value ? ` in product ${selectedProduct.value}` : "");
      let activeSessionId = sessionId || await startSession({ force: false });
      let agentResponse;

      try {
        agentResponse = await askAssistant(messageText, activeSessionId);
      } catch (error) {
        let errorData = get(error, 'response.data', '');
        if (errorData instanceof ArrayBuffer) {
          try { errorData = JSON.parse(new TextDecoder().decode(errorData)); } catch { /* ignore */ }
        }
        const errorDetail = get(errorData, 'detail', '') || (typeof errorData === 'string' ? errorData : '');
        const isSessionNotFound = error?.response?.status === 404
          && typeof errorDetail === "string"
          && errorDetail.toLowerCase().includes('session not found');

        if (!isSessionNotFound) {
          throw error;
        }

        activeSessionId = await startSession({ force: true });

        if (!activeSessionId) {
          throw error;
        }

        agentResponse = await askAssistant(messageText, activeSessionId);
      }

      if (agentResponse.data) {
        const stream = new ReadableStream({
          start(controller) {
            controller.enqueue(new Uint8Array(agentResponse.data));
            controller.close();
          }
        });

        const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

        let buffer = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }

          buffer += value;
          let lastNewlineIndex;

          // Process lines until the last full SSE message
          while ((lastNewlineIndex = buffer.indexOf('\n\n')) !== -1) {
            const eventChunk = buffer.substring(0, lastNewlineIndex);
            buffer = buffer.substring(lastNewlineIndex + 2); // +2 for the two newlines

            const lines = eventChunk.split('\n');
            let data = [];
            let event = 'message'; // Default event type
            let id = null;
            let retry = null;

            for (const line of lines) {
              if (line.startsWith('data:')) {
                data.push(line.substring(5).trim());
              } else if (line.startsWith('event:')) {
                event = line.substring(6).trim();
              } else if (line.startsWith('id:')) {
                id = line.substring(3).trim();
              } else if (line.startsWith('retry:')) {
                retry = parseInt(line.substring(6).trim(), 10);
              }
            }

            // Construct and process the SSE message
            const sseMessage = {
              event: event,
              data: JSON.parse(data.join('\n')), // Join multiple data lines
              id: id,
              retry: retry,
            };

            if (event === 'message') {
              /** @type {{ data: AssistAgentResponse }} */
              const { data: { id: eventId, invocationId, partial, content: { parts } } } = sseMessage;

              parts.forEach(({ text, functionResponse }, index) => {
                const result = functionResponse?.name === "api_data_agent" && functionResponse?.response?.result;
                
                if (text || result) {
                  let lastValue = agentMessages[`${agentId}-${index}`];

                  if (!lastValue) {
                    lastValue = {
                      id: `${agentId}-${index}`,
                      type: "assistant",
                      timestamp: new Date(),
                      text: "",
                      status: "partial",
                      isGreeting: text && text.toLowerCase().includes("hello"),
                      eventId,
                      invocationId,
                    };
                  }

                  const nextValue = { ...lastValue };

                  if (partial) {
                    nextValue.text += text || result;
                    nextValue.status = "partial";
                  } else {
                    nextValue.text = text || result;
                    nextValue.status = "complete";
                  }

                  agentMessages[`${agentId}-${index}`] = nextValue;
                  dispatch(upsertMessages({ ...nextValue, timestamp: new Date() }));
                }
              });
            }
          }
        }
      } else {
        throw new Error("Failed to retrieve response from assistant.");
      }
    } catch (error) {
      // TODO - change this message based on the error code when the information gets added.
      dispatch(
          upsertMessages({
            id: agentMessage.id,
            timestamp: new Date(),
            text: "Error retrieving response from assistant. Please try your request again.",
            status: "error",
          })
        );
    } finally {
      setModalOpen(true);
    }
  };

  /** @type { (id: string, reaction?: "up" | "down") => Promise<void> } */
  const updateFeedback = async (id, reaction) => {
    const { eventId, invocationId } = messages.find((msg) => msg.id === id) || {};

    if (eventId && invocationId) {
      dispatch(upsertMessages({ id, reaction }));

      try {
        await axios.post(feedbackEndpoint, {
          sessionId, eventId, invocationId, reaction
        }, {
          headers: {
            "Content-Type": "application/json",
          },
        });
      } catch (error) {
        // TODO - handle error
      }
    }
  };

  const fetchAdpProducts = async () => {
    try {
      /** @type {{ data: Array<{ label: string, value: string }>, status: number }} */
      const { data, status } = await axios.get(`${import.meta.env.VITE_API_PATH}/${region || "us"}/products`, {
        params: {
          onlyProductCodes: true
        }
      });

      if (status === 200) {
        const adpProductsItems = data.map(item => ({ label: getSorName(item.label), value: item.value }));

        setProducts(adpProductsItems);

        //default select to WFN from products or first product in the list if WFN is not present
        const wfnProduct = adpProductsItems.find(item => item.value === 'hcm-offrg-wfn');
        setSelectedProduct(wfnProduct || adpProductsItems[0]);
      } else {
        setProductsError({ message: `[${status}] Could not retrieve products data successfully.` });
      }
    } catch (e) {
      setProductsError({ message: get(e, 'response.data.message', 'Something went wrong when trying to get products data. Please try again.') });
    }
  };

  useEffect(() => {
    if (!boxOpen && !modalOpen) {
      return;
    }

    startSession();
  }, [boxOpen, modalOpen, sessionEndpoint, sessionId]);

  useEffect(() => {
    if (wait > 0) {
      const timer = setTimeout(() => {
        if (wait - 1 <= 0) setWait(0);
        else setWait(wait - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [wait]);

  useEffect(() => {
    if (isAdmin) {
      fetchAdpProducts();
    }
  }, [isAdmin, region]);

  return (
    <AssistContext.Provider
      value={{
        sessionId, messages, addUserMessage, updateFeedback, modalOpen, setModalOpen,
        boxOpen, setBoxOpen, wait, isAdmin, initials, products, productsError,
        selectedProduct, setSelectedProduct
      }}
    >
      {children}
    </AssistContext.Provider>
  );
};

export default AssistProvider;
export { AssistContext, maxMessageLength, waitTime };
